import {
  ElectronPrintRequest,
  PrintResult,
  IPPConfig,
  PrinterCapabilities,
  PrinterConnectionReport,
  RenderToPdfPayload,
  RenderToPdfResult,
  PrintQueueEventPayload,
  BookletImpositionOptions,
  BookletImpositionResult,
  NoticeCoverStamp,
  PdfStampStyle,
  AnnotatePdfResult,
} from '../types';
import type { PrintJob } from '../types';

export class ElectronPrintingApi {
  static async isAvailable(): Promise<boolean> {
    return !!(window.electronAPI?.printing);
  }

  static async connect(config: IPPConfig): Promise<PrinterConnectionReport> {
    if (!window.electronAPI?.printing) {
      throw new Error('Electron printing API not available');
    }
    
    return await window.electronAPI.printing.connect(config);
  }

  static async print(request: ElectronPrintRequest): Promise<PrintResult> {
    if (!window.electronAPI?.printing) {
      throw new Error('Electron printing API not available');
    }
    
    return await window.electronAPI.printing.print(request);
  }

  static async getJobStatus(jobId: number): Promise<any> {
    if (!window.electronAPI?.printing) {
      throw new Error('Electron printing API not available');
    }
    
    return await window.electronAPI.printing.getStatus(jobId);
  }

  static async disconnect(): Promise<void> {
    if (!window.electronAPI?.printing) {
      throw new Error('Electron printing API not available');
    }
    
    return await window.electronAPI.printing.disconnect();
  }

  static async validateCapabilities(requirements: Partial<PrinterCapabilities>): Promise<boolean> {
    if (!window.electronAPI?.printing) {
      throw new Error('Electron printing API not available');
    }
    
    return await window.electronAPI.printing.validateCapabilities(requirements);
  }

  static async renderToPDF(payload: RenderToPdfPayload): Promise<RenderToPdfResult> {
    if (!window.electronAPI?.printing) {
      throw new Error('Electron printing API not available');
    }

    return await window.electronAPI.printing.renderToPDF(payload);
  }

  static async imposeBooklet(
    inputPath: string,
    job: Pick<PrintJob, 'type' | 'metadata'>,
    options?: BookletImpositionOptions,
  ): Promise<BookletImpositionResult> {
    if (!window.electronAPI?.printing?.imposeBooklet) {
      throw new Error('Electron booklet imposition API not available');
    }

    return await window.electronAPI.printing.imposeBooklet(inputPath, options, {
      type: job.type,
      profileId: job.metadata.profileId,
    });
  }

  static onQueueEvent(listener: (payload: PrintQueueEventPayload) => void): () => void {
    if (!window.electronAPI?.printing?.onQueueEvent) {
      return () => {};
    }
    return window.electronAPI.printing.onQueueEvent(listener);
  }

  static supportsPdfAnnotation(): boolean {
    return typeof window.electronAPI?.printing?.annotatePdf === 'function';
  }

  static async annotatePdfCover(
    inputPath: string,
    stamp: NoticeCoverStamp,
    style?: PdfStampStyle,
  ): Promise<AnnotatePdfResult> {
    if (!window.electronAPI?.printing?.annotatePdf) {
      throw new Error('Electron PDF annotation API not available');
    }

    return await window.electronAPI.printing.annotatePdf({
      inputPath,
      stamp,
      style,
    });
  }

  static createDefaultConfig(): IPPConfig {
    // Prefer persisted settings if available
    try {
      const raw = localStorage.getItem('printing.ippConfig');
      if (raw) {
        const parsed = JSON.parse(raw);
        const persistedCaps = parsed?.capabilities ?? {};
        const capabilities: PrinterCapabilities = {
          supportsA3: persistedCaps?.supportsA3 ?? true,
          supportsFolding: persistedCaps?.supportsFolding ?? true,
          supportsStapling: persistedCaps?.supportsStapling ?? true,
          supportsOffsetStacking: persistedCaps?.supportsOffsetStacking ?? true,
          supportsSaddleStitch: persistedCaps?.supportsSaddleStitch ?? true,
          a3Ready: persistedCaps?.a3Ready ?? true,
          finisher: typeof persistedCaps?.finisher === 'string' ? persistedCaps.finisher : 'DF-3030',
        };

        const withDefaults: IPPConfig = {
          host: parsed?.host ?? 'localhost',
          port: typeof parsed?.port === 'number' ? parsed.port : 8631,
          scheme: parsed?.scheme === 'ipps' ? 'ipps' : 'ipp',
          resource: typeof parsed?.resource === 'string' ? parsed.resource : '/ipp/print',
          timeoutMs: typeof parsed?.timeoutMs === 'number' ? parsed.timeoutMs : 15000,
          capabilities,
        };
        return withDefaults;
      }
    } catch {
      // ignore and fall back to defaults
    }

    // Fallback defaults (dev-friendly)
    return {
      host: 'localhost',
      port: 8631,
      scheme: 'ipp',
      resource: '/ipp/print',
      timeoutMs: 15000,
      capabilities: {
        supportsA3: true,
        supportsFolding: true,
        supportsStapling: true,
        supportsOffsetStacking: true,
        supportsSaddleStitch: true,
        a3Ready: true,
        finisher: 'DF-3030',
      },
    };
  }
}
